/**
 * DiarioProducao - Timeline de registros do diário de produção naval
 * Inspirado no modelo de interações do CRM, adaptado para contexto de obra naval
 */
import React, { useState, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  BookOpen, Plus, Loader2, Edit2, Trash2, Upload, X, Image,
  Anchor, AlertTriangle, Search, Wrench, TestTube, ShieldAlert,
  Users, Truck, RefreshCw, FileText, Paperclip, ChevronDown, Calendar, Mic, MicOff, Square, CheckCircle2, Info
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';
import AnexosManager from '../AnexosManager';
import AnexosErrorBoundary from '../AnexosErrorBoundary';

const API = process.env.REACT_APP_BACKEND_URL;

const TIPOS_DIARIO = {
  avanco_producao: { label: "Avanço de Produção", cor: "#16a34a", icon: Anchor },
  problema: { label: "Problema / Não Conformidade", cor: "#dc2626", icon: AlertTriangle },
  inspecao: { label: "Inspeção / Qualidade", cor: "#2563eb", icon: Search },
  material: { label: "Material / Insumo", cor: "#d97706", icon: Wrench },
  teste_ensaio: { label: "Teste / Ensaio", cor: "#7c3aed", icon: TestTube },
  seguranca: { label: "Segurança", cor: "#ea580c", icon: ShieldAlert },
  reuniao: { label: "Reunião / Alinhamento", cor: "#0891b2", icon: Users },
  entrega: { label: "Entrega / Expedição", cor: "#059669", icon: Truck },
  mudanca_status: { label: "Mudança de Status", cor: "#6b7280", icon: RefreshCw },
  observacao_geral: { label: "Observação Geral", cor: "#64748b", icon: FileText },
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
      ', ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  } catch { return dateStr; }
};

const formatDateShort = (dateStr) => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return dateStr; }
};

// Helpers compartilhados de preview
const canPreviewAnexo = (tipo, nome) => {
  const ext = (nome || '').split('.').pop()?.toLowerCase() || '';
  if ((tipo || '').includes('pdf') || ext === 'pdf') return 'pdf';
  if ((tipo || '').includes('image') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'image';
  return null;
};

const resolveAnexoUrl = (anexo) => {
  const url = anexo?.url || '';
  if (!url) return '';
  if (url.startsWith('blob:') || url.startsWith('data:')) return url;
  if (url.startsWith('/api/')) return `${API}${url}`;
  return url;
};

// Card de uma entrada do diário na timeline
const DiarioCard = ({ entry, onEdit, onDelete }) => {
  const tipoConfig = TIPOS_DIARIO[entry.tipo] || TIPOS_DIARIO.observacao_geral;
  const IconComponent = tipoConfig.icon;
  const [previewAnexo, setPreviewAnexo] = useState(null);

  const handlePreview = (anexo) => {
    if (canPreviewAnexo(anexo.tipo, anexo.nome)) {
      setPreviewAnexo(anexo);
    } else {
      window.open(resolveAnexoUrl(anexo), '_blank');
    }
  };

  return (
    <div className="flex gap-3 group" data-testid={`diario-entry-${entry.id}`}>
      {/* Ícone na timeline */}
      <div className="flex flex-col items-center">
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
          style={{ backgroundColor: tipoConfig.cor + '18', border: `2px solid ${tipoConfig.cor}` }}
        >
          <IconComponent className="h-4 w-4" style={{ color: tipoConfig.cor }} />
        </div>
        <div className="w-px flex-1 bg-gray-200 mt-1" />
      </div>

      {/* Conteúdo */}
      <div className="flex-1 pb-5">
        <div className="bg-white border rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow">
          {/* Header */}
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge
                className="text-xs font-medium px-2 py-0.5"
                style={{ backgroundColor: tipoConfig.cor + '18', color: tipoConfig.cor, border: `1px solid ${tipoConfig.cor}40` }}
              >
                {tipoConfig.label}
              </Badge>
              <span className="text-xs text-gray-400">{formatDate(entry.data)}</span>
            </div>
            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {entry.tipo !== 'mudanca_status' && (
                <>
                  <button onClick={() => onEdit(entry)} className="p-1 text-gray-400 hover:text-blue-600" title="Editar">
                    <Edit2 className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => onDelete(entry.id)} className="p-1 text-gray-400 hover:text-red-600" title="Excluir">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Descrição */}
          <p className="text-sm text-gray-700 whitespace-pre-line">{entry.conteudo}</p>

          {/* Anexos */}
          {entry.anexos?.length > 0 && (
            <div className="flex flex-col gap-1 mt-2">
              {entry.anexos.map(anexo => {
                const dataFormatada = (() => {
                  if (!anexo.uploaded_at) return null;
                  try {
                    const d = new Date(anexo.uploaded_at);
                    if (isNaN(d.getTime())) return null;
                    return d.toLocaleString('pt-BR', {
                      day: '2-digit', month: '2-digit', year: 'numeric',
                      hour: '2-digit', minute: '2-digit'
                    });
                  } catch { return null; }
                })();
                return (
                  <div key={anexo.id} className="flex items-center gap-2 group/anexo" data-testid={`diario-anexo-row-${anexo.id}`}>
                    <button
                      type="button"
                      onClick={() => handlePreview(anexo)}
                      className={`flex items-center gap-1 text-xs px-2 py-1 border rounded transition-colors cursor-pointer max-w-[260px] ${
                        anexo.perdido
                          ? 'bg-red-50 border-red-300 text-red-700 hover:bg-red-100'
                          : 'bg-gray-50 hover:bg-blue-50 hover:border-blue-300'
                      }`}
                      title={anexo.perdido ? 'Reenvie este arquivo' : (canPreviewAnexo(anexo.tipo, anexo.nome) ? 'Visualizar' : 'Abrir em nova aba')}
                      data-testid={`diario-anexo-${anexo.id}`}
                    >
                      {anexo.tipo?.startsWith('image/') ? <Image className="h-3 w-3 text-blue-500" /> : <Paperclip className="h-3 w-3 text-gray-500" />}
                      <span className="truncate">{anexo.nome_exibicao || anexo.nome}</span>
                      {anexo.perdido && (
                        <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse shrink-0"></span>
                      )}
                    </button>
                    {(dataFormatada || anexo.uploaded_by || anexo.s3_key) && (
                      <span className="text-[10px] text-gray-400 flex items-center gap-1">
                        {dataFormatada && (
                          <span>
                            {dataFormatada}
                            {anexo.uploaded_by && ` · ${anexo.uploaded_by}`}
                          </span>
                        )}
                        {anexo.s3_key && (
                          <span
                            className="cursor-help text-gray-400 hover:text-blue-500"
                            title={`Identificação do arquivo no S3 Amazon:\n${anexo.s3_key}`}
                            data-testid={`diario-anexo-s3-info-${anexo.id}`}
                          >
                            <Info className="h-3 w-3" />
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Próxima ação */}
          {entry.proxima_acao && (
            <div className="mt-2 px-2.5 py-1.5 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800 flex items-center gap-2">
              <span className="font-medium">Próxima ação:</span>
              <span>{entry.proxima_acao}</span>
              {entry.data_proxima_acao && (
                <span className="text-amber-600">({formatDateShort(entry.data_proxima_acao)})</span>
              )}
            </div>
          )}

          {/* Autor */}
          <div className="mt-2 text-xs text-gray-400">
            por {entry.responsavel || entry.usuario_nome}
          </div>
        </div>
      </div>

      {/* Modal de Preview inline (PDF/Imagem) */}
      <Dialog open={!!previewAnexo} onOpenChange={(o) => !o && setPreviewAnexo(null)}>
        <DialogContent className="max-w-5xl w-[95vw] h-[90vh] p-0 flex flex-col" data-testid="diario-anexo-preview-dialog">
          <DialogHeader className="px-4 py-3 border-b flex-row items-center justify-between space-y-0">
            <DialogTitle className="text-sm font-medium truncate pr-4">
              {previewAnexo?.nome_exibicao || previewAnexo?.nome}
            </DialogTitle>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => previewAnexo && window.open(resolveAnexoUrl(previewAnexo), '_blank')}
            >
              Nova aba
            </Button>
          </DialogHeader>
          <div className="flex-1 bg-gray-100 overflow-hidden">
            {previewAnexo && canPreviewAnexo(previewAnexo.tipo, previewAnexo.nome) === 'pdf' && (
              <iframe
                src={resolveAnexoUrl(previewAnexo)}
                className="w-full h-full border-0"
                title={previewAnexo.nome}
              />
            )}
            {previewAnexo && canPreviewAnexo(previewAnexo.tipo, previewAnexo.nome) === 'image' && (
              <div className="w-full h-full flex items-center justify-center p-4 overflow-auto">
                <img
                  src={resolveAnexoUrl(previewAnexo)}
                  alt={previewAnexo.nome}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Dialog para criar/editar registro
const DialogRegistro = ({ open, onClose, entry, projetoId, embarcacaoIndex, currentUser, onSaved }) => {
  const [tipo, setTipo] = useState(entry?.tipo || 'observacao_geral');
  const [conteudo, setConteudo] = useState(entry?.conteudo || '');
  const [responsavel, setResponsavel] = useState(entry?.responsavel || currentUser?.full_name || '');
  const [data, setData] = useState(entry?.data?.split('T')[0] || new Date().toISOString().split('T')[0]);
  const [proximaAcao, setProximaAcao] = useState(entry?.proxima_acao || '');
  const [dataProximaAcao, setDataProximaAcao] = useState(entry?.data_proxima_acao?.split('T')[0] || '');
  const [files, setFiles] = useState([]);
  const [anexosLocal, setAnexosLocal] = useState(Array.isArray(entry?.anexos) ? entry.anexos : []);
  const [saving, setSaving] = useState(false);
  const [editingResponsavel, setEditingResponsavel] = useState(false);
  const fileInputRef = useRef(null);

  // Gravação de áudio
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        if (audioBlob.size < 1000) { toast.error('Áudio muito curto'); return; }

        setIsTranscribing(true);
        try {
          const formData = new FormData();
          formData.append('file', audioBlob, 'audio.webm');
          // FormData: browser define Content-Type com boundary automaticamente
          const res = await api.post('/diario-producao/transcrever', formData);
          if (res.data?.text) {
            setConteudo(prev => prev ? prev + '\n' + res.data.text : res.data.text);
            toast.success('Áudio transcrito!');
          }
        } catch (err) {
          console.error('Erro na transcrição:', err);
          toast.error('Erro ao transcrever áudio');
        } finally {
          setIsTranscribing(false);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (err) {
      console.error('Erro ao acessar microfone:', err);
      toast.error('Permissão de microfone negada');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const handleSave = async () => {
    if (!conteudo.trim()) { toast.error('Descrição é obrigatória'); return; }
    setSaving(true);
    try {
      // Combinar data escolhida com hora local atual para gerar ISO completo
      // (evita que o Input type="date" gere UTC midnight e apareça em timezone errado)
      const buildDataISO = (dateStr, existingISO) => {
        try {
          if (!dateStr) return new Date().toISOString();
          // Se editando e a data é a mesma, preservar hora original
          if (existingISO && existingISO.split('T')[0] === dateStr) return existingISO;
          const [y, m, d] = dateStr.split('-').map(Number);
          const now = new Date();
          const combined = new Date(y, (m || 1) - 1, d || 1, now.getHours(), now.getMinutes(), now.getSeconds());
          return combined.toISOString();
        } catch {
          return new Date().toISOString();
        }
      };
      const dataISO = buildDataISO(data, entry?.data);

      let result;
      if (entry?.id) {
        // Edição
        const res = await api.put(`/diario-producao/${entry.id}`, { tipo, conteudo, responsavel, proxima_acao: proximaAcao, data_proxima_acao: dataProximaAcao || null, data: dataISO });
        result = res.data;
      } else {
        // Criação
        const res = await api.post('/diario-producao', {
          projeto_id: projetoId,
          embarcacao_index: embarcacaoIndex,
          tipo,
          conteudo,
          responsavel,
          data: dataISO,
          proxima_acao: proximaAcao,
          data_proxima_acao: dataProximaAcao || null,
        });
        result = res.data;
      }

      // Upload de arquivos novos (pending)
      const entryId = result.id || entry?.id;
      if (files.length > 0 && entryId) {
        for (const file of files) {
          const formData = new FormData();
          formData.append('file', file);
          // FormData: browser define Content-Type com boundary automaticamente
          await api.post(`/diario-producao/${entryId}/upload`, formData);
        }
      }

      // Liberar blob URLs dos pendentes após upload
      try {
        anexosLocal.forEach(a => {
          if (a?._blobUrl) URL.revokeObjectURL(a._blobUrl);
        });
      } catch { /* noop */ }

      toast.success(entry?.id ? 'Registro atualizado!' : 'Registro adicionado!');
      onSaved();
      onClose();
    } catch (err) {
      console.error('Erro ao salvar diário:', err);
      toast.error(err.response?.data?.detail || 'Erro ao salvar registro');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-amber-600" />
            {entry?.id ? 'Editar Registro' : 'Novo Registro'} — Diário de Produção
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Tipo */}
          <div className="space-y-2">
            <Label>Tipo de Registro</Label>
            <Select value={tipo} onValueChange={setTipo}>
              <SelectTrigger data-testid="diario-tipo-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(TIPOS_DIARIO).filter(([k]) => k !== 'mudanca_status').map(([key, cfg]) => (
                  <SelectItem key={key} value={key}>
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: cfg.cor }} />
                      {cfg.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Data + Responsável */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Data</Label>
              <Input type="date" value={data} onChange={e => setData(e.target.value)} data-testid="diario-data" />
            </div>
            <div className="space-y-2">
              <Label>Responsável</Label>
              <div className="h-9 px-3 py-1.5 border rounded-md bg-gray-50 text-sm flex items-center text-gray-700">
                {responsavel || currentUser?.full_name || 'Não definido'}
              </div>
            </div>
          </div>

          {/* Descrição */}
          <div className="space-y-2">
            <Label>Descrição *</Label>
            <div className="relative">
              <Textarea
                value={conteudo}
                onChange={e => setConteudo(e.target.value)}
                placeholder="Descreva o registro ou use o microfone para ditar..."
                rows={4}
                data-testid="diario-conteudo"
                className="pr-14"
              />
              <div className="absolute right-2 top-2 flex flex-col gap-1">
                {isRecording ? (
                  <button
                    type="button"
                    onClick={stopRecording}
                    className="w-9 h-9 rounded-full bg-green-500 text-white flex items-center justify-center shadow-lg animate-pulse"
                    title="Concluir gravação"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                  </button>
                ) : isTranscribing ? (
                  <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center">
                    <Loader2 className="h-4 w-4 animate-spin text-amber-600" />
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={startRecording}
                    className="w-9 h-9 rounded-full bg-gray-100 hover:bg-blue-100 text-gray-500 hover:text-blue-600 flex items-center justify-center transition-colors"
                    title="Gravar áudio e transcrever"
                  >
                    <Mic className="h-4 w-4" />
                  </button>
                )}
              </div>
              {isRecording && (
                <div className="absolute bottom-2 left-2 flex items-center gap-1.5 text-xs text-red-600 font-medium">
                  <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                  Gravando... Toque no check verde para concluir
                </div>
              )}
            </div>
          </div>

          {/* Próxima ação */}
          <div className="space-y-2">
            <Label className="text-xs text-gray-500">Próxima Ação (opcional)</Label>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <Input
                  value={proximaAcao}
                  onChange={e => setProximaAcao(e.target.value)}
                  placeholder="Ex: Aguardar material, reteste de motor..."
                  data-testid="diario-proxima-acao"
                />
              </div>
              <Input type="date" value={dataProximaAcao} onChange={e => setDataProximaAcao(e.target.value)} data-testid="diario-data-proxima" />
            </div>
          </div>

          {/* Upload de fotos/anexos - padronizado com AnexosManager */}
          <AnexosErrorBoundary>
            <AnexosManager
              anexos={anexosLocal}
              onChange={setAnexosLocal}
              maxFiles={20}
              maxSizeMB={50}
              label="Fotos / Anexos"
              uploadHandler={async (file) => {
                // Upload deferido: armazena arquivo em memória e gera blob URL
                // (funciona pra qualquer tipo: PDF, imagem, Word, Excel)
                const blobUrl = URL.createObjectURL(file);
                setFiles(prev => [...prev, file]);
                return {
                  id: `pending-${Date.now()}-${file.name}`,
                  nome: file.name,
                  nome_exibicao: file.name.replace(/\.[^/.]+$/, ''),
                  url: blobUrl,
                  tipo: file.type,
                  tamanho: file.size,
                  data_upload: new Date().toISOString(),
                  _pending: true,
                  _fileName: file.name,
                  _blobUrl: blobUrl,
                };
              }}
              deleteHandler={async (anexo) => {
                // Se é um anexo pending (ainda não enviado), remover da lista de files e liberar blob URL
                if (anexo._pending && anexo._fileName) {
                  setFiles(prev => prev.filter(f => f.name !== anexo._fileName));
                  if (anexo._blobUrl) {
                    try { URL.revokeObjectURL(anexo._blobUrl); } catch { /* noop */ }
                  }
                  return;
                }
                // Anexo já persistido: deletar no backend se estamos editando
                if (entry?.id && anexo?.id) {
                  try {
                    await api.delete(`/diario-producao/${entry.id}/anexo/${anexo.id}`);
                  } catch (err) {
                    console.error('Erro ao remover anexo:', err);
                    toast.error('Erro ao remover anexo');
                    throw err;
                  }
                }
              }}
            />
          </AnexosErrorBoundary>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={handleSave}
            disabled={!conteudo.trim() || saving}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="diario-salvar-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
            {entry?.id ? 'Atualizar' : 'Adicionar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Componente principal: Timeline do Diário
const DiarioProducao = ({ projetoId, embarcacaoIndex, currentUser }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [filterTipo, setFilterTipo] = useState('todos');
  const [expanded, setExpanded] = useState(true);

  const fetchEntries = useCallback(async () => {
    if (!projetoId) return;
    setLoading(true);
    try {
      const res = await api.get(`/diario-producao/${projetoId}?embarcacao_index=${embarcacaoIndex}`);
      setEntries(res.data || []);
    } catch { }
    finally { setLoading(false); }
  }, [projetoId, embarcacaoIndex]);

  React.useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const handleDelete = async (entryId) => {
    if (!window.confirm('Excluir este registro?')) return;
    try {
      await api.delete(`/diario-producao/${entryId}`);
      setEntries(prev => prev.filter(e => e.id !== entryId));
      toast.success('Registro excluído');
    } catch { toast.error('Erro ao excluir'); }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setShowDialog(true);
  };

  const filtered = filterTipo === 'todos' ? entries : entries.filter(e => e.tipo === filterTipo);
  const tipoCounts = {};
  entries.forEach(e => { tipoCounts[e.tipo] = (tipoCounts[e.tipo] || 0) + 1; });

  if (!projetoId) return null;

  return (
    <div className="mt-6" data-testid="diario-producao-section">
      {/* Header */}
      <div
        className="flex items-center justify-between cursor-pointer select-none"
        onClick={() => setExpanded(!expanded)}
      >
        <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-amber-600" />
          Diário de Produção ({entries.length})
          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${expanded ? '' : '-rotate-90'}`} />
        </h3>
        <Button
          size="sm"
          onClick={(e) => { e.stopPropagation(); setEditingEntry(null); setShowDialog(true); }}
          className="bg-amber-600 hover:bg-amber-700"
          data-testid="diario-novo-btn"
        >
          <Plus className="h-3.5 w-3.5 mr-1" /> Registrar
        </Button>
      </div>

      {expanded && (
        <div className="mt-3">
          {/* Filtros por tipo */}
          {entries.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4">
              <button
                onClick={() => setFilterTipo('todos')}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${filterTipo === 'todos' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
              >
                Todos ({entries.length})
              </button>
              {Object.entries(TIPOS_DIARIO).map(([key, cfg]) => {
                const count = tipoCounts[key];
                if (!count) return null;
                return (
                  <button
                    key={key}
                    onClick={() => setFilterTipo(filterTipo === key ? 'todos' : key)}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${filterTipo === key ? 'text-white' : 'bg-white hover:bg-gray-50'}`}
                    style={filterTipo === key ? { backgroundColor: cfg.cor, borderColor: cfg.cor } : { borderColor: cfg.cor + '60', color: cfg.cor }}
                  >
                    {cfg.label.split(' / ')[0]} ({count})
                  </button>
                );
              })}
            </div>
          )}

          {/* Timeline */}
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-amber-600" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">
              {entries.length === 0 ? 'Nenhum registro no diário. Clique em "+ Registrar" para começar.' : 'Nenhum registro com este filtro.'}
            </div>
          ) : (
            <div className="mt-2">
              {filtered.map(entry => (
                <DiarioCard key={entry.id} entry={entry} onEdit={handleEdit} onDelete={handleDelete} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Dialog */}
      {showDialog && (
        <DialogRegistro
          open={showDialog}
          onClose={() => { setShowDialog(false); setEditingEntry(null); }}
          entry={editingEntry}
          projetoId={projetoId}
          embarcacaoIndex={embarcacaoIndex}
          currentUser={currentUser}
          onSaved={fetchEntries}
        />
      )}
    </div>
  );
};

export default DiarioProducao;
